<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  referenceNumber: number;
}>();

const { t } = useI18n();

const rootRef = ref<HTMLElement>();

onMounted(() => {
  nextTick(() => rootRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
});

function handleBackToHome() {
  console.log('Revenir à l\'accueil');
}
</script>

<template>
  <div ref="rootRef" class="confirmation-container">
    <div class="confirmation-header">
      <svg width="81" height="81" viewBox="0 0 81 81" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="40.2" cy="40.2002" r="37.5" fill="#D7FFE8" />
        <path
          d="M28.95 40.2002L36.45 47.7002L51.45 32.7002"
          stroke="#24B47E" stroke-width="5.4" stroke-linecap="round" stroke-linejoin="round"
        />
      </svg>

      <h2 class="title">
        {{ t('cart.confirmation.title') }}
      </h2>
      <p class="subtitle">
        {{ t('cart.confirmation.subtitle') }}
      </p>

      <div class="reservation-badge">
        {{ t('cart.confirmation.reservationNumber') }} : <strong>{{ props.referenceNumber }}</strong>
      </div>
    </div>

    <div class="content-block">
      <div class="next-steps-card">
        <h3 class="next-steps-title">
          {{ t('cart.confirmation.nextSteps') }}
        </h3>

        <div class="steps">
          <div class="step">
            <span class="step-number">1</span>
            <div class="step-content">
              <p class="step-title">
                {{ t('cart.confirmation.step1Title') }}
              </p>
              <p class="step-description">
                {{ t('cart.confirmation.step1Description') }}
              </p>
            </div>
          </div>

          <div class="step">
            <span class="step-number">2</span>
            <div class="step-content">
              <p class="step-title">
                {{ t('cart.confirmation.step2Title') }}
              </p>
              <p class="step-description">
                {{ t('cart.confirmation.step2Description') }}
              </p>
            </div>
          </div>

          <div class="step">
            <span class="step-number">3</span>
            <div class="step-content">
              <p class="step-title">
                {{ t('cart.confirmation.step3Title') }}
              </p>
              <p class="step-description">
                {{ t('cart.confirmation.step3Description') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <button type="button" class="back-to-home-button" @click="handleBackToHome">
        {{ t('cart.confirmation.backToHome') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="css">
.confirmation-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--yb-spacing-base);
  gap: var(--yb-spacing-base);
}

.confirmation-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--yb-spacing-xs);
}

.title {
  font-size: var(--yb-text-xl);
  font-weight: var(--yb-text-bold);
  color: var(--yb-color-black);
  margin: var(--yb-spacing-sm) 0 0 0;
}

.subtitle {
  font-size: var(--yb-text-sm);
  color: var(--yb-color-darkgray);
  margin: 0;
}

.reservation-badge {
  margin-top: var(--yb-spacing-sm);
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: var(--yb-color-lightgray);
  border-radius: var(--yb-radius-sm);
  font-size: var(--yb-text-sm);
  color: var(--yb-color-black);
}

.content-block {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--yb-spacing-base);
}

.next-steps-card {
  display: flex;
  flex-direction: column;
  border: var(--yb-color-mediumgray) solid 1px;
  padding: var(--yb-spacing-sm);
  border-radius: var(--yb-radius-base);
  box-sizing: border-box;
}

.next-steps-title {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-bold);
  color: var(--yb-color-black);
  margin: 0 0 var(--yb-spacing-base) 0;
}

.steps {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-xl);
}

.step {
  display: flex;
  align-items: flex-start;
  gap: var(--yb-spacing-sm);
}

.step-number {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--yb-color-light-primary);
  color: var(--yb-color-primary);
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-semi-bold);
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-title {
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-bold);
  color: var(--yb-color-black);
  margin: 0;
}

.step-description {
  font-size: var(--yb-text-sm);
  color: var(--yb-color-darkgray);
  margin: 0;
}

.back-to-home-button {
  width: 100%;
  height: 56px;
  background-color: var(--yb-color-primary);
  color: var(--yb-button-text-color);
  border: none;
  border-radius: var(--yb-radius-sm);
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.back-to-home-button:hover {
  opacity: 0.9;
}

@media (prefers-reduced-motion: reduce) {
  .back-to-home-button {
    transition: none;
  }
}
</style>
